
import React, { useState, useEffect, useMemo, useRef } from 'react'

import { Stack, Popover } from '@mui/material'

import { useScanRouting } from 'contexts'
import { SearchSelect, Search } from 'components'
// Direct file import, NOT the `components` barrel — this file is part of that barrel, so importing
// back through it is a cycle (see the same note in `ActionLine`, where it threw at runtime).
import { ScanTargetButton } from '../ScanTargetButton'
import type { ScanFilterEntity } from 'services'
import { FilterField } from './FilterField'
import { filterDropdownPaperSx } from './dropdownSurface'

/**
 * Which entity a scanned tag resolves to, per filter field.
 *
 * A tag is a physical object; a filter is about an ENTITY, and which entity depends on the field.
 * Product → tag → certificate → its product (so scanning one bag means "show me every bag like
 * this"). Owner → its owner. Certificate / ID → the certificate itself.
 *
 * A field NOT in this map gets no scan control at all. That is a fix, not an omission: the button
 * used to render on every string column and then resolve nothing on most of them — including the
 * Product column, whose rows have no `.tag` for the old local match to find. A control that
 * cannot work is worse than no control.
 */
const SCAN_ENTITY_BY_FIELD: Record<string, ScanFilterEntity> = {
  certificate: 'certificate',
  certificates: 'certificate',
  id: 'certificate',
  product: 'product',
  products: 'product',
  owner: 'owner',
  owners: 'owner',
  customer: 'owner',
  // The scanned tag IS the value here, so this one needs no server resolution beyond the
  // `tagId` every resolution already returns — see `projectResolution`, which handles `tag`
  // before the `found` guard because an unassigned tag is still a real tag.
  tag: 'tag',
  tags: 'tag',
}

/** The bits of a DataGrid column this field actually reads. */
interface SearchFilterColumn {
  field?: string
  headerName?: string
  /**
   * The option list, supplied by the page INSTEAD of being derived from `rows`.
   *
   * `rows` is the drawer's ONE shared array — every field reads the same one — which is fine for
   * a grid-backed list where the facets are properties of the rows on screen, and impossible for
   * a surface whose facets are independent entities. The analytics page is the latter: it has no
   * grid at all, its Owner / Product / Certificate lists come from three separate queries, and
   * `rows` is `[]`. Without this the field renders a permanently empty dropdown.
   *
   * Entries are the same shape a row would be — full entity objects, so `searchParams.preview`
   * draws the same `OwnerPreview` / `ProductPreview` / `CertificatePreview` rows the certificates
   * drawer draws, and `valueOf` reads the same `.id`.
   *
   * OPT-IN and absent everywhere else: no `type: 'string'` column in the app carries `options`
   * today (the only `options` on a non-multiSelect column is `SubscriptionsPage`'s `statusChip`),
   * so this cannot alter any existing drawer.
   */
  options?: any[]
  /**
   * Which entity a scan resolves to here, when the field NAME cannot say.
   *
   * `SCAN_ENTITY_BY_FIELD` keys on the field name, which works while the field is called `owner`
   * or `product`. A page whose fields are named for the API params they map to (`ownerIds`,
   * `productIds`, `certificateIds`) matches nothing and silently loses its scan control — the
   * field is about an owner, the map just cannot tell. Declaring it is the fix; guessing from a
   * pluralised/suffixed name is not.
   */
  scanEntity?: ScanFilterEntity
  [key: string]: any
}

interface SearchFilterProps {
  /** Filter mode emits `{field, operator, value}`; `notFilter` mode emits the picked item(s). */
  onChange?: (value: any) => void
  onSearch?: () => void
  column?: SearchFilterColumn
  rows?: any[],
  notFilter?: boolean
  filter?: any[],
  singleSelect?: boolean
  /** Visible field label. Falls back to `column.headerName`. Lets the field read
   *  "Transfer to" while `column.headerName` stays a clean noun ("Owner"). */
  label?: string
  /** Force the scan control off on a field that would otherwise get one. */
  showScan?: boolean
  /** Override the entity a scan resolves to here (defaults to the field-name map above). */
  scanEntity?: ScanFilterEntity
  /**
   * Per-row trailing node, forwarded straight to `SearchSelect` (which already supports it —
   * `AddOwnerModal` / `AddModal` use it for the source badge). Added here so a `notFilter`
   * picker built on this field can annotate a row (VRG-282's `Default tag` badge) WITHOUT
   * forking the list into a second, hand-rolled dropdown.
   */
  listItemSecondaryAction?: (item: any) => React.ReactNode
  /**
   * An extra row rendered at the bottom of the dropdown's list, given the text
   * currently typed into THIS field.
   *
   * A callback rather than a node because the search text is this component's own
   * state — a caller wanting to react to it (the transfer modal offers an invite row
   * once what you have typed is a valid email address) has no other way to see it.
   * `close` is handed over for the same reason: picking a real option closes the
   * dropdown via `SearchSelect`'s own apply path, and a footer row is not an option,
   * so it has to be able to dismiss the popover itself or picking it would leave the
   * list hanging open over the selection it just made.
   */
  listFooter?: (ctx: { search: string; close: () => void }) => React.ReactNode
  searchParams? : {
    fields?: string[],
    preview?: string | null,
    reference?: string
    /**
     * The ROW key holding the id the SERVER filters by, when it differs from the reference
     * object's own `.id`.
     *
     * Certificates' Owner column is why this exists. Its option is `row.customer`, and
     * `customer` is the USERS row — the certificates query joins `certificates → owners →
     * users` and returns the user under that name — so `customer.id` is a USER id. The list's
     * `ownerId` param filters `certificates.owner_id`, which is an OWNERS id, carried on the
     * row as `row.owner_id`. Emitting `customer.id` therefore filtered to zero rows every
     * time: a real id, of the wrong entity.
     *
     * Product and Certificate need nothing here — for them the reference object's own `.id`
     * IS what the server is keyed on (`productId`, `certificateIds`) — which is exactly why
     * this is opt-in rather than a change to how every column resolves its value.
     */
    valueField?: string
  }
}

export const SearchFilter = (props: SearchFilterProps) => {
  const {
    column,
    rows = [],
    onChange = () => {},
    filter = [],
    singleSelect = false,
    notFilter = false,
    showScan = true,
    scanEntity,
    label,
    listItemSecondaryAction,
    listFooter,
    onSearch = () => {},
  } = props
  const { searchParams = { fields: [], preview: null, reference: undefined } } = props

  // set to the column's field if no arr of search fields provided
  if (!searchParams.fields?.length) {
    searchParams.fields = [column?.field].filter(Boolean) as string[]
  }

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [open, setOpen] = useState(false);
  // The field + scan-button row. A CLICK anchors the dropdown to this same element (it is the
  // element the row's onClick fires on); a SCAN has no click to read it from, so it is held here
  // and the two paths open an identically positioned and identically sized dropdown.
  const fieldRowRef = useRef<HTMLDivElement | null>(null)
  const [selectedValues, setSelectedValues] = useState<any[]>(filter)
  // VERA-455 E — the filter-mode field's own search text; drives the dropdown list.
  const [query, setQuery] = useState('')

  const fieldName = String(column?.field ?? column?.headerName ?? '').trim().toLowerCase()
  // Prop first (a call site rendering this field directly), then the column's own declaration,
  // then the field-name map. See `SearchFilterColumn.scanEntity`.
  const entity = scanEntity ?? column?.scanEntity ?? SCAN_ENTITY_BY_FIELD[fieldName]
  // Stable key for this field across mounts — the filter drawer unmounts its fields when it
  // closes, so the key (not the component) is what the routing layer holds scans against.
  const targetKey = String(column?.field ?? column?.headerName ?? '')
  const fieldLabel = label ?? column?.headerName

  const scanning = useScanRouting()
  const scanEnabled = showScan && Boolean(entity)
  // Whether THIS field is the aimed one is decided inside `ScanTargetButton` (kind AND key, since
  // the toolbar search is a target too) — this component only needs to know what has landed here.
  const scannedRows = scanEnabled ? scanning.scannedFor(targetKey) : []

  // Tell the routing layer this field is on screen. A scan that lands while the drawer is open
  // needs no announcement (the checkbox IS the report); one that lands while it is closed does.
  useEffect(() => {
    if (!scanEnabled) return
    return scanning.registerField(targetKey)
  }, [scanEnabled, targetKey, scanning.registerField])

  useEffect(() => {
    if (notFilter == false) {
      setSelectedValues(filter);
    }
  }, [filter])

  /**
   * The dropdown's OPTIONS, and the VALUE each one filters by.
   *
   * `searchParams.reference` means the option is a field of the row rather than the row itself
   * (the certificates list carries its owner as `row.customer`, its product as `row.product`), and
   * `SearchSelect` dereferences it when rendering. Everything that has to agree with what the user
   * clicked — the checked set, the emitted value, the injected scan result — has to dereference
   * the same way, or the check never appears and the filter is emitted as a list of `undefined`.
   */
  const referenceKey = searchParams.reference
  const optionOf = (row: any) => (referenceKey ? row?.[referenceKey] : row)
  // `item[field] ?? item.id`: a plain string column ('name') filters by its own text, exactly as
  // before. A column whose option is an OBJECT (Product, Owner, Certificate) has no such key —
  // `row.product['product']` is undefined — so it falls back to the id, which is what the server
  // filters are keyed on (`productId`, `ownerId`, `certificateIds`).
  const valueOf = (option: any) =>
    (column?.field ? option?.[column.field] : undefined) ?? option?.id
  // The filter value of a ROW, for every path that still has the row in hand. `valueField`
  // (see the prop's doc) wins when the column declares one; everything else resolves exactly
  // as before, so a column without it is untouched.
  const valueField = searchParams.valueField
  const valueOfRow = (row: any) =>
    (valueField ? row?.[valueField] : undefined) ?? valueOf(optionOf(row))

  /**
   * Every option this field has seen, not just the ones surviving the current filter.
   *
   * `rows` is the FILTERED page, and these options are what DRIVE that filter — so ticking one
   * owner narrowed `rows` to that owner and every other owner vanished from the list. You could
   * not add a second or switch without clearing first, and while the Owner filter was still
   * emitting the wrong id (so nothing matched) the list emptied to "No results" entirely.
   *
   * Retaining what has already been loaded breaks that loop. It is a union of pages seen, NOT a
   * complete index: an entity that has never appeared in a loaded page still will not be listed
   * (unchanged from before — these selects have always been fed the bounded page). Making the
   * list globally complete needs a dedicated options endpoint per column, which this is not.
   */
  const seenRowsRef = useRef<Map<any, any>>(new Map())

  /**
   * A page-supplied option list REPLACES `rows`, and skips the retention below.
   *
   * Retention exists to break a feedback loop that only occurs when the options are derived
   * FROM the filtered rows (see the note on `knownRows`). Supplied options come from their own
   * queries and are not narrowed by this filter, so there is no loop — and retaining them would
   * be actively wrong, resurrecting entries the page has deliberately dropped.
   */
  const suppliedOptions = Array.isArray(column?.options) ? (column!.options as any[]) : null
  const baseRows = suppliedOptions ?? rows

  const knownRows = useMemo(() => {
    if (suppliedOptions) return suppliedOptions
    const idOf = (row: any) => optionOf(row)?.id
    // Idempotent (set-if-absent), so StrictMode's double-invoke is harmless.
    rows.forEach(row => {
      const id = idOf(row)
      if (id != null && !seenRowsRef.current.has(id)) seenRowsRef.current.set(id, row)
    })
    const currentIds = new Set(rows.map(idOf).filter(id => id != null))
    // Current rows first — they carry the freshest copy of an entity that is in both.
    const retained = Array.from(seenRowsRef.current.entries())
      .filter(([id]) => !currentIds.has(id))
      .map(([, row]) => row)
    return retained.length ? [...rows, ...retained] : rows
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, referenceKey, suppliedOptions])

  const optionRows = useMemo(() => {
    if (!scannedRows.length) return knownRows
    // Compared against `ResolvedEntity.id`, which is documented as "the value the filter is
    // keyed on" — so the comparison has to be against the row's FILTER VALUE, not its option's
    // id. On a `valueField` column those are different entities (the Owner filter is keyed on
    // an owners id; the option is a user), and comparing the option's id would report a
    // loaded owner as missing and inject a duplicate row for the same person.
    const present = new Set(
      knownRows.map(row => valueOfRow(row)).filter(Boolean),
    )
    // INJECT what the scan resolved when the loaded page does not contain it. These selects hold
    // a bounded page, so a scanned tag can easily resolve to something that is not in it, and
    // checking an option that does not exist checks nothing at all — the silent no-op this
    // guards against.
    const missing = scannedRows
      .filter(row => !present.has(row.id))
      .map(row => {
        if (!referenceKey) return row.option
        // A synthesised row must carry `valueField` too, or a scanned entity would show up
        // checked and then emit `undefined`. `ResolvedEntity.id` IS that value: for Owner it
        // is the owners id the server wants, which the option itself does not carry.
        return valueField
          ? { [referenceKey]: row.option, [valueField]: row.id }
          : { [referenceKey]: row.option }
      })
    return missing.length ? [...knownRows, ...missing] : knownRows
  }, [knownRows, scannedRows, referenceKey, valueField])

  /**
   * option.id → that row's filter value, for the paths that only have an OPTION.
   *
   * `SearchSelect` dereferences `reference` itself and hands OPTIONS back, so by the time a tick
   * is reported the row that carried `valueField` is gone. This is how it is recovered — without
   * it the emitted value and the checked state would disagree, which is the same class of bug
   * (checkbox says one thing, query says another) that `valueField` exists to end.
   *
   * Empty unless the column declares a `valueField`; `valueOfOption` then falls straight through
   * to `valueOf`. First row wins on a repeated option id, matching the dedupe in `options` below.
   */
  const valueByOptionId = useMemo(() => {
    const map = new Map<any, any>()
    if (!valueField) return map
    optionRows.forEach(row => {
      const id = optionOf(row)?.id
      const value = row?.[valueField]
      if (id == null || value == null || map.has(id)) return
      map.set(id, value)
    })
    return map
  }, [optionRows, referenceKey, valueField])

  const valueOfOption = (option: any) =>
    (option?.id != null ? valueByOptionId.get(option.id) : undefined) ?? valueOf(option)

  const options = useMemo(() => {
    const seen = new Set<string>()
    const out: any[] = []
    optionRows.forEach(row => {
      const option = optionOf(row)
      if (!option) return
      // One row per entity: many certificates share a product, and the Product dropdown wants
      // that product listed once.
      const id = option?.id
      if (id != null) {
        if (seen.has(id)) return
        seen.add(id)
      }
      out.push(row)
    })
    return out
  }, [optionRows, referenceKey])

  const selectedItems = useMemo(
    () =>
      options
        .map(optionOf)
        .filter(option => option && (selectedValues ?? []).includes(valueOfOption(option))),
    [options, selectedValues, valueByOptionId],
  )

  /**
   * Report this field's value UP, without touching the dropdown.
   *
   * Split out of `handleApply` because the filter drawer no longer has an Apply: a checkbox
   * click has to reach the staged copy the moment it happens, while the operator carries on
   * ticking boxes in an open dropdown. Emitting and closing were one step only because the
   * single caller did both.
   */
  const emitFilterValues = (items: any[]) => {
    // `items` are OPTIONS — `SearchSelect` dereferences `reference` before reporting — so the
    // value is resolved through `valueOfOption`, the same resolver `selectedItems` checks
    // against. Both must read the same way or the ticked box and the applied filter diverge.
    const values = items.map((item: any) => valueOfOption(item))
    // Unchecking a scanned option has to take it OUT of the scanned set too, or the next scan's
    // re-report would put it straight back and the checkbox would look broken. This is what
    // replaces the old modal's per-row X: removal happens in the dropdown, like every other
    // filter value.
    if (scanEnabled) scanning.retainScanned(targetKey, values)
    onChange({'field': column?.field, 'operator': 'isAnyOf', 'value': values})
  }

  const handleApply = (items: any[]) => {
    handleClose()
    {/* TODO: remove this fix for usage outside of Filters -  we should make SearchSelect include the popover menu as a prop rather than have alll that logic in SearchFilter. Once compelte, edit SetTagForProducts modal */}
    if (notFilter) {
      onChange(items)
    } else {
      emitFilterValues(items)
    }
  }

  /**
   * Fold what has been scanned into this field's value.
   *
   * Filter mode stages it like any other checked option, so the drawer's Apply button lights up
   * and the chip/Clear affordances work with no special case. Single-select pickers (Transfer's
   * "Transfer to", Create-certificates' product) take the resolved entity as the pick instead.
   */
  const appliedScanRef = useRef<string | null>(null)
  const scannedIds = scannedRows.map(row => row.id).join(',')
  useEffect(() => {
    if (!scanEnabled || !scannedRows.length) return
    if (notFilter || singleSelect) {
      const latest = scannedRows[scannedRows.length - 1]
      if (appliedScanRef.current === latest.id) return
      appliedScanRef.current = latest.id
      const loaded = baseRows.map(optionOf).find(option => option?.id === latest.id)
      onChange(singleSelect ? (loaded ?? latest.option) : [loaded ?? latest.option])
      scanning.clearScanned(targetKey)
      return
    }
    // `ResolvedEntity.id` is already the value the filter is keyed on (the server resolves a tag
    // to the OWNERS row, not to the user behind it), so it is staged as-is. That is why a scanned
    // owner narrowed the list correctly while a ticked one did not — the two paths were emitting
    // different entities, and `valueField` is what makes the click path agree with this one.
    const next = Array.from(new Set([...(selectedValues ?? []), ...scannedRows.map(r => r.id)]))
    const current = selectedValues ?? []
    if (next.length === current.length) return
    setSelectedValues(next)
    onChange({ 'field': column?.field, 'operator': 'isAnyOf', 'value': next })
    // SHOW the operator where the scan landed. Checking a box inside a closed menu is invisible:
    // the only feedback was the count badge on a field they were not necessarily looking at. The
    // dropdown opens on the same anchor a click uses, so the newly checked option is on screen.
    //
    // Nothing more is needed to APPLY it: the `onChange` above stages the value in the drawer,
    // and the drawer commits every staged change on its own debounce. A scan used to report
    // itself separately (`onScanApplied`) because a scan auto-applied and a click did not — that
    // asymmetry is gone, so the report is too.
    if (fieldRowRef.current) {
      setAnchorEl(fieldRowRef.current)
      setOpen(true)
    }
  }, [scannedIds, selectedValues])

  /**
   * The picker's deferred open, and why it has to be CANCELLABLE.
   *
   * `notFilter` mode waits ~400ms before opening so the anchor row has finished animating in
   * with its modal. That timer used to be fire-and-forget, and nothing cleared it — so any
   * close landing inside its window (Escape, a click on the popover's backdrop, picking a row)
   * ran `handleClose`, which nulls `anchorEl`, and then the orphan timer re-opened the Popover
   * with NO ANCHOR. MUI cannot position an anchorless popover, so it falls back to the viewport
   * origin offset by its `marginThreshold` — the dropdown appeared 16px from the LEFT EDGE of
   * the screen, detached from the field and escaping the modal (measured: left 16 instead of
   * 391), and narrow, because the paper's width is derived from `anchorEl.clientWidth` too.
   *
   * Reproducing it takes nothing exotic: two clicks on the field inside 400ms (natural, since
   * the first click shows nothing for 400ms) arm a second timer, and any close before it fires
   * re-opens the list in the corner.
   */
  const openTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const cancelDeferredOpen = () => {
    if (openTimerRef.current === null) return
    clearTimeout(openTimerRef.current)
    openTimerRef.current = null
  }
  // The timer must die with the component too — this field lives in modals that are routinely
  // closed mid-interaction.
  useEffect(() => cancelDeferredOpen, [])

  // In modal (notFilter) mode, anchor to the whole field + scan-button row so the dropdown
  // aligns left and spans its full width directly below it — the row IS `fieldRowRef`, so the
  // anchor is read from the ref rather than from a click that happened 400ms ago. That also
  // makes a detached anchor impossible: the element is resolved (and checked) at open time.
  const resolveAnchor = (): HTMLElement | null => {
    const row = fieldRowRef.current
    if (!row) return null
    const anchor = (notFilter ? row.parentElement ?? row : row) as HTMLElement
    return anchor.isConnected ? anchor : null
  }

  const handleClick = (event: React.MouseEvent) => {
    onSearch()
    // Open immediately in filter mode (type-to-filter); keep the small settle delay for
    // the modal picker where the anchor row animates in.
    if (notFilter) {
      cancelDeferredOpen()
      openTimerRef.current = setTimeout(() => {
        openTimerRef.current = null
        const anchor = resolveAnchor()
        if (!anchor) return
        setAnchorEl(anchor)
        setOpen(true)
      }, 400)
      return
    }
    setAnchorEl(event.currentTarget as HTMLElement)
    setOpen(true)
  };

  const handleClear = () => {
    // Clearing the field clears what was scanned into it as well — otherwise the scanned set
    // would immediately re-apply itself and the Clear would appear to do nothing.
    if (scanEnabled) scanning.clearScanned(targetKey)
    setSelectedValues([])
    /**
     * PICKER MODE REPORTS NOTHING FROM HERE.
     *
     * `SearchSelect` calls `onClear` as its OWN internal reset, immediately after reporting the
     * pick: `handleSelect(item)` → `onApply(item)` → `handleClear()` → `onClear()`. So on this
     * path the filter descriptor below landed on the caller ONE RENDER AFTER the item did, and
     * overwrote it — every `notFilter` picker ended up holding
     * `{field: 'product', operator: 'isAnyOf', value: []}` instead of the thing that was picked.
     * That object is truthy and carries no `title`/`sku`/`images`/`id`, which is why the selected
     * card rendered as an empty "No Image" tile with no title and no SKU, and why the id sent on
     * save was `undefined`.
     *
     * `notFilter` mode's documented contract is "emits the picked item(s)" (see the `onChange`
     * prop), and an internal reset is not a pick — so it stays silent. Nothing is lost: picker
     * mode never populates `selectedValues`, so `showClear`/`count` are always empty there and
     * the field's own Clear affordance is filter-mode only.
     */
    if (notFilter) return
    onChange({'field': column?.field, 'operator': 'isAnyOf', 'value': []})
  }

  const handleClose = () => {
    // FIRST — a pending deferred open outlives this close otherwise, and re-opens the popover
    // after `anchorEl` has been nulled. See `openTimerRef`.
    cancelDeferredOpen()
    setAnchorEl(null);
    setOpen(false)
  };

  /**
   * Escape closes the dropdown.
   *
   * MUI already does this — but from a keydown bound to the MODAL subtree, and this Popover sets
   * `disableAutoFocus`/`disableEnforceFocus` so the anchor field stays typeable while the list is
   * open. Focus therefore never enters the popover, that handler never sees the key, and Escape
   * did nothing at all (verified in the browser before this was added).
   *
   * Listening on `document` catches it wherever focus actually is. CAPTURE phase, and the event
   * is stopped, so the innermost open layer wins: Escape closes this dropdown without also
   * closing the filters drawer behind it.
   */
  useEffect(() => {
    if (!open) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      e.stopPropagation()
      handleClose()
    }
    document.addEventListener('keydown', onKeyDown, true)
    return () => document.removeEventListener('keydown', onKeyDown, true)
  }, [open])

  /**
   * The TARGET SELECTOR. Not a modal opener: it says WHERE the next scan lands.
   *
   * Clicking another field's icon moves the target there — exactly one field receives scans at a
   * time. Clicking this one again stops routing, which leaves nothing aimed and so ends the
   * session. If nothing is connected yet, the first click also opens the pairing modal (or, with
   * a desk reader plugged in, opens the session silently and shows no modal at all).
   *
   * The control itself is `ScanTargetButton`, shared with the list toolbar — the two are the same
   * control on two surfaces and must not drift (they had: this copy described two states while
   * the toolbar's described three). Everything specific to this field is passed in.
   */
  const scanButton = scanEnabled ? (
    <ScanTargetButton
      target={{
        kind: 'filter-field',
        key: targetKey,
        entity: entity as ScanFilterEntity,
        label: String(fieldLabel ?? 'this field'),
      }}
      // Named as it reads mid-sentence: "Send scans to the Owner filter".
      destination={fieldLabel ? `the ${fieldLabel} filter` : 'this field'}
    />
  ) : undefined

  return (
    <FilterField
      label={fieldLabel}
      showClear={selectedItems.length != 0}
      onClear={handleClear}
      // The count badge `FilterField` already owns (brand purple, same as MultiSelect/StatusChip)
      // — this field simply never passed it, so a scan-checked option changed nothing visible in
      // the title row. Counted off `selectedItems`, the same source as `showClear` above, so the
      // badge and the Clear affordance can never disagree about whether this field has a value.
      count={selectedItems.length}
    >
      {/* VERA-455 E — ONE inline search for both the filter drawer AND the modal picker
          (e.g. Transfer): type in THIS field and the dropdown below lists the filtered
          options (no second search box). Iconless in the filter drawer (D); keeps the
          magnifier in the modal picker (not the filters menu). Scan button preserved. */}
      <Stack ref={fieldRowRef} direction="row" spacing={2} onClick={handleClick} sx={{ cursor: 'pointer' }}>
        <Stack sx={{ flex: 1, minWidth: 0 }}>
          <Search
            showIcon={notFilter}
            fullWidth
            debounce={false}
            onChange={(v) => setQuery(v)}
            onClear={() => setQuery('')}
            placeholder="Search"
            sx={{ minWidth: 0 }}
          />
        </Stack>
        {scanButton}
      </Stack>
      <Popover
        id={open ? 'simple-popover' : undefined}
        // `&& anchorEl` is the backstop, not the fix (that is `openTimerRef` above): an
        // anchorless Popover does not fail loudly, it silently paints itself in the viewport's
        // corner. Refusing to render one means a future path that loses the anchor shows
        // nothing rather than a dropdown floating off in the top-left.
        open={open && Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        // The search lives in the anchor field (outside this popover); don't let the
        // popover steal/trap focus, so the field stays typeable while the list is open.
        disableAutoFocus
        disableEnforceFocus
        // Render above any modal (e.g. the Transfer certificate modal) so the
        // results dropdown isn't clipped/covered by it.
        sx={{ zIndex: (theme) => theme.zIndex.modal + 1 }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            // Shared with CompanyFilter — same panel, same field beneath it, one definition.
            // `width` matches the anchored field (+ scan) row so the dropdown lines up with it.
            sx: (theme) =>
              filterDropdownPaperSx(
                theme,
                anchorEl ? (anchorEl as HTMLElement).clientWidth : undefined,
                anchorEl as HTMLElement | null,
              ),
          },
        }}
      >
       <SearchSelect
        items={options}
        selected={selectedItems}
        singleSelect={singleSelect}
        source='filters'
        hoverGradient
        searchParams={searchParams}
        hideSearch
        externalSearch={query}
        listItemSecondaryAction={listItemSecondaryAction}
        listFooter={listFooter?.({ search: query, close: handleClose })}
        // FILTER MODE COMMITS AS YOU TICK. The drawer applies on a debounce, so a checkbox has
        // nothing to wait for and the dropdown's Apply would be a button that does what has
        // already happened. `notFilter` (the modal pickers) keeps its Apply — there the picked
        // items are a submission, not a filter, and there is a real moment to confirm.
        autoApply={!notFilter}
        onChange={(items) => emitFilterValues(items)}
        onApply={(items) => handleApply(items)}
        // Clear survives the loss of Apply because it is an ACTION, not a commit — and it
        // matters more now, since auto-apply means there is nothing to undo. It empties the
        // field for real (and drops what was scanned into it), rather than only closing.
        onClear={() => { handleClear(); handleClose() }}
        // Dismiss WITHOUT touching the selection — what the auto-apply footer's "Done" needs.
        // Previously unpassed, so it fell through to SearchSelect's no-op default and the
        // button did nothing; the only labelled exit was Clear, which emptied the field.
        onClose={handleClose}
        />
      </Popover>
    </FilterField>
  )
}
